import { Link } from 'react-router-dom';
import { UserProfile } from '@shared/types';
import { useAuthListener } from '../../hooks/useAuthListener';
import { auth, db } from '../../firebase/firebase';
import { User } from 'firebase/auth';
import { useState } from 'react';

interface HeaderProps {
  userProfile?: UserProfile | null;
  user?: User | null; // Firebase User type
  hideSignInButton?: boolean; // Hide sign in button when on login page
  hideSignUpButton?: boolean; // Hide sign up button when on signup page
}

export function Header({ userProfile, user: propUser, hideSignInButton = false, hideSignUpButton = false }: HeaderProps) {
  const { signOut, user: hookUser, userProfile: hookUserProfile, loading } = useAuthListener(auth, db);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Use prop user if available, otherwise use hook user
  const user = propUser || hookUser;
  // Use prop userProfile if available, otherwise use hook userProfile
  const finalUserProfile = userProfile || hookUserProfile;
  
  // Show loading spinner if user is signed in but profile is still loading
  // Also check if profile exists but doesn't have a role (incomplete)
  const isLoadingProfile = user && (!finalUserProfile || !finalUserProfile.role) && loading;

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsMobileMenuOpen(false); // Close mobile menu after sign out
    } catch {
      // Handle sign out error silently in production
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img 
                src="/shared/assets/images/logo/logo.svg" 
                alt="WaitlistPoker Logo" 
                className="h-16 w-auto mr-2"
                onError={(e) => {
                  // Fallback to the original design if logo fails to load
                  const target = e.currentTarget;
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) {
                    target.style.display = 'none';
                    fallback.style.display = 'flex';
                  }
                }}
              />
              <div className="h-16 w-16 bg-primary-600 rounded-lg flex items-center justify-center mr-2 hidden">
                <span className="text-white font-bold text-2xl">♠</span>
              </div>
              <span className="text-xl font-bold text-gray-900">WaitlistPoker</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex space-x-8">
            <Link
              to="/"
              className="bg-black text-white hover:bg-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              Poker Rooms
            </Link>
            {finalUserProfile?.role === 'admin' || finalUserProfile?.role === 'superadmin' ? (
              <Link
                to="/admin"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                Admin
              </Link>
            ) : null}
            {finalUserProfile?.role === 'superadmin' ? (
              <Link
                to="/superadmin"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                Superadmin
              </Link>
            ) : null}
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                {/* Mobile Hamburger Menu Button */}
                <button
                  onClick={toggleMobileMenu}
                  className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Open mobile menu"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>

                {/* Desktop User Menu - Hidden on mobile */}
                <div className="hidden md:flex items-center space-x-4">
                  {isLoadingProfile ? (
                    <div className="flex items-center space-x-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      <p className="text-sm text-gray-600">Loading profile...</p>
                    </div>
                  ) : finalUserProfile?.role ? (
                    <div className="flex items-center space-x-4">
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">{finalUserProfile.name}</p>
                        <p className="text-gray-500 capitalize">{finalUserProfile.role}</p>
                      </div>
                      <Link to="/profile" className="btn btn-outline">
                        Profile
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="btn btn-outline"
                      >
                        Sign Out
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-4">
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">{user.displayName || 'User'}</p>
                        <p className="text-gray-500">Complete Profile</p>
                      </div>
                      <Link to="/create-profile" className="btn btn-primary">
                        Complete Profile
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="btn btn-outline"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                {!hideSignInButton && (
                  <Link to="/login" className="btn btn-outline">
                    Sign In
                  </Link>
                )}
                {!hideSignUpButton && (
                  <Link to="/signup" className="btn btn-primary">
                    Sign Up
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay and Drawer */}
      {isMobileMenuOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={closeMobileMenu}
          />
          
          {/* Drawer */}
          <div className="fixed top-0 right-0 h-full w-80 max-w-sm bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out md:hidden">
            <div className="flex flex-col h-full">
              {/* Drawer Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                  {finalUserProfile && (
                    <p className="text-sm text-gray-500 capitalize">{finalUserProfile.role}</p>
                  )}
                </div>
                <button
                  onClick={closeMobileMenu}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Close mobile menu"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* User Info */}
              <div className="p-6 border-b border-gray-200">
                {isLoadingProfile ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                    <p className="text-sm text-gray-600">Loading profile...</p>
                  </div>
                ) : finalUserProfile?.role ? (
                  <div>
                    <p className="font-medium text-gray-900">{finalUserProfile.name}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium text-gray-900">{user?.displayName || 'User'}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                    <p className="text-sm text-gray-500">Complete Profile</p>
                  </div>
                )}
              </div>

              {/* Navigation Links */}
              <div className="flex-1 px-6 py-4 space-y-2">
                <Link
                  to="/"
                  onClick={closeMobileMenu}
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors duration-200"
                >
                  <svg className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Poker Rooms
                </Link>

                {finalUserProfile?.role === 'admin' || finalUserProfile?.role === 'superadmin' ? (
                  <Link
                    to="/admin"
                    onClick={closeMobileMenu}
                    className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors duration-200"
                  >
                    <svg className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Admin
                  </Link>
                ) : null}

                {finalUserProfile?.role === 'superadmin' ? (
                  <Link
                    to="/superadmin"
                    onClick={closeMobileMenu}
                    className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors duration-200"
                  >
                    <svg className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    Superadmin
                  </Link>
                ) : null}

                <Link
                  to={finalUserProfile?.role ? "/profile" : "/create-profile"}
                  onClick={closeMobileMenu}
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors duration-200"
                >
                  <svg className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {finalUserProfile?.role ? "Profile" : "Complete Profile"}
                </Link>
              </div>

              {/* Sign Out Button */}
              <div className="p-6 border-t border-gray-200">
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center justify-center px-4 py-3 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors duration-200"
                >
                  <svg className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
}
